/*
 * @file jobPerLink.tsx
 * @fileoverview Exports the job per link feature related utils.
 */
import { useLazyQuery } from '@apollo/client';
import { useState } from 'react';
import { IntlShape } from 'react-intl';
import StringWidget from '~/jobiqo/components/Form/Widgets/String';
import TextLongWidget from '~/jobiqo/components/Form/Widgets/TextLong';
import PREVIEW_LINK_CONTENT from '~/jobiqo/components/Job/graphql/queries/previewLinkContent.gql';
import JobTypeEnum from '~/jobiqo/enums/jobType';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { LoadingCircleIcon } from '~/vcl/icons/LoadingCircle';

/**
 * It returns an object that contains overrides for the job link and job
 * description fields if the current job type is JOB_PER_LINK.
 * @param {string} jobType - The job type.
 * @param {IntlShape} intl - IntlShape - This is the intl object that is passed to
 * the component.
 * @returns An object with two keys, field_job_link and field_job_description.
 */
export function getJobFormJobPerLinkOverrides(
  jobType: string,
  intl: IntlShape,
) {
  if (!(jobType === JobTypeEnum.JOB_PER_LINK)) {
    return null;
  }

  // Flag that determines if the description field should be manually populated.
  const [populateDescriptionField, setPopulateDescriptionField] = useState(
    false,
  );

  const [previewLinkContent, { data, error, loading }] = useLazyQuery(
    PREVIEW_LINK_CONTENT,
    {
      onCompleted() {
        if (!error) {
          // There is no error, populate the description field.
          setPopulateDescriptionField(true);
        }
      },
    },
  );

  return {
    field_job_link: ({ field, setFieldValue }) => (
      <div key={field.id}>
        <StringWidget
          onChange={(e) => {
            setFieldValue(field.id, e.target.value);
            previewLinkContent({
              variables: { url: e.target.value },
            });
          }}
          required={true}
          entityField={field}
        />
        <Spacing size="medium" />
      </div>
    ),

    field_job_description: ({ field, setFieldValue }) => {
      // Manually updated field value.
      if (populateDescriptionField) {
        setPopulateDescriptionField(false);
        setFieldValue(field.id, data.previewContent);
      }

      return (
        <div key={field.id}>
          {loading && (
            <LoadingCircleIcon
              color="primary"
              height="100"
              width="100"
              title={intl.formatMessage({
                id: 'loading_message',
                defaultMessage: 'Loading',
              })}
            />
          )}
          <TextLongWidget entityField={field} />
          <Spacing size="medium" />
        </div>
      );
    },
  };
}
